import { Landmark } from "lucide-react";
import { useState, useEffect, type ChangeEvent } from "react";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import type { FormErrors, MandateForm, SetField } from "../types";

const PRESET_BANKS = [
  "HDFC Bank",
  "ICICI Bank",
  "State Bank of India",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank",
  "Yes Bank",
  "IDFC First Bank",
];

const BANK_OPTIONS = [
  ...PRESET_BANKS.map((b) => ({ value: b, label: b })),
  { value: "OTHER", label: "Other Bank (Enter manually)" },
];

const BANK_BRANCHES: Record<string, string> = {
  "HDFC Bank": "HDFC Bank — Connaught Place Branch, New Delhi",
  "ICICI Bank": "ICICI Bank — Bandra Kurla Complex Branch, Mumbai",
  "State Bank of India": "State Bank of India — Rohini Sector 24 Branch, Delhi",
  "Axis Bank": "Axis Bank — Koramangala Branch, Bengaluru",
  "Kotak Mahindra Bank": "Kotak Mahindra Bank — Fort Branch, Mumbai",
  "Punjab National Bank": "Punjab National Bank — Karol Bagh Branch, New Delhi",
  "Bank of Baroda": "Bank of Baroda — Alipore Branch, Kolkata",
  "Canara Bank": "Canara Bank — MG Road Branch, Bengaluru",
  "Union Bank of India": "Union Bank of India — Gariahat Branch, Kolkata",
  "IndusInd Bank": "IndusInd Bank — Andheri West Branch, Mumbai",
  "Yes Bank": "Yes Bank — Worli Branch, Mumbai",
  "IDFC First Bank": "IDFC First Bank — Powai Branch, Mumbai",
};

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

interface Props {
  form: MandateForm;
  errors: FormErrors;
  setField: SetField;
  setError: (key: keyof MandateForm, message: string) => void;
}

export default function BankStep({ form, errors, setField, setError }: Props) {
  const [isOtherBank, setIsOtherBank] = useState<boolean>(() => {
    return !!form.bankName && !PRESET_BANKS.includes(form.bankName);
  });

  useEffect(() => {
    if (form.bankName && !PRESET_BANKS.includes(form.bankName)) {
      setIsOtherBank(true);
    }
  }, [form.bankName]);

  const validIfsc = IFSC_RE.test(form.ifsc);
  const resolved = validIfsc && form.bankName ? (BANK_BRANCHES[form.bankName] ?? form.bankName) : null;

  function handleAccountChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value.replace(/\D/g, "").slice(0, 18);
    setField("accountNumber", next);
  }

  function handleConfirmBlur() {
    const val = form.confirmAccountNumber.trim();
    if (val && val !== form.accountNumber) {
      setError("confirmAccountNumber", "Account numbers do not match");
    } else {
      setError("confirmAccountNumber", "");
    }
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Bank Details</h3>
      <p className="text-[13px] text-ink-secondary">
        Account that will be debited for the monthly EMI
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <Select
            label="Bank Name"
            required
            placeholder="Select bank"
            options={BANK_OPTIONS}
            error={!isOtherBank ? errors.bankName : undefined}
            id="bankName"
            value={isOtherBank ? "OTHER" : (PRESET_BANKS.includes(form.bankName) ? form.bankName : "")}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "OTHER") {
                setIsOtherBank(true);
                setField("bankName", "");
              } else {
                setIsOtherBank(false);
                setField("bankName", val);
              }
            }}
          />
        </div>

        {isOtherBank && (
          <div className="md:col-span-2">
            <Input
              label="Enter Bank Name"
              required
              placeholder="e.g. Bank of Maharashtra, Federal Bank, Central Bank of India"
              error={errors.bankName}
              id="customBankName"
              value={form.bankName}
              onChange={(e) => setField("bankName", e.target.value)}
              helper="Enter the customer's bank name as printed on their cheque or passbook"
            />
          </div>
        )}

        <div className="md:col-span-2">
          <FieldWrapper label="Account Type" required error={errors.accountType}>
            <div id="accountType" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(["Savings", "Current"] as const).map((t) => {
                const selected = form.accountType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setField("accountType", t)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left transition-colors cursor-pointer ${
                      selected
                        ? "border-primary-500 bg-primary-50"
                        : "border-slate-300 bg-white hover:border-slate-400"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-ink">{t} Account</p>
                      <p className="text-xs text-ink-secondary">
                        {t === "Savings" ? "For personal savings" : "For business transactions"}
                      </p>
                    </div>
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        selected ? "border-primary-500" : "border-slate-300"
                      }`}
                    >
                      {selected && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </FieldWrapper>
        </div>

        <FieldWrapper
          label="Account Number"
          required
          error={errors.accountNumber}
          htmlFor="accountNumber"
          helper="Enter the account number carefully — it cannot be changed later"
        >
          <input
            id="accountNumber"
            inputMode="numeric"
            maxLength={18}
            value={form.accountNumber}
            onChange={handleAccountChange}
            placeholder="Enter account number"
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.accountNumber
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>

        <Input
          label="Confirm Account Number"
          required
          error={errors.confirmAccountNumber}
          id="confirmAccountNumber"
          inputMode="numeric"
          maxLength={18}
          placeholder="Re-enter account number"
          value={form.confirmAccountNumber}
          onChange={(e) => setField("confirmAccountNumber", e.target.value.replace(/\D/g, ""))}
          onBlur={handleConfirmBlur}
        />

        <Input
          label="IFSC Code"
          required
          error={errors.ifsc}
          id="ifsc"
          maxLength={11}
          placeholder="Format: ABCD0123456"
          helper="Format: ABCD0123456"
          value={form.ifsc}
          onChange={(e) => setField("ifsc", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        />

        {resolved && (
          <div className="flex items-center gap-2.5 rounded-lg bg-info-bg px-3.5 py-2.5 md:col-span-2">
            <Landmark className="h-4 w-4 shrink-0 text-info" />
            <p className="text-[13px] text-ink-secondary">{resolved}</p>
          </div>
        )}
      </div>
    </div>
  );
}